// =============================================================================
// AXIS ASSOCIATION RULINGS — AXIS-P2 (docs/design/axis-lens-plan-2026-07-06.md
// §5 "Tags visible + correctable", THE SYNTHESIS v2 phasing P2 row: "tags
// visible + correctable; gate pinned"). The terminal-correction ledger for a
// user's ruling on ONE achievement/facet's ONE axis tag.
//
// SHAPE + REASONING COPIED WHOLESALE from achievement_alignment_rulings.ts
// (alignment_review.ts) — per corpus-proving-ground.md §4's own explicit
// recommendation ("(B), the ruling-ledger shape... NOT (A) [bucketSource]"):
//   1. The plan's deniability guarantee needs "asked, declined" distinguishable
//      from "never asked" — a ledger's absence-of-a-row IS "never asked"; a
//      row with ruling='user-declined' IS "asked, said no, permanently done".
//      A bucketSource-style two-state flag cannot represent this distinction.
//   2. The near-miss association's EVIDENCE must be frozen at ruling time
//      (evidenceSnapshot) so a later cue-list change never retroactively
//      rewrites what the user was actually shown when they ruled.
//   3. More than two ruling shapes exist (confirmed / declined / removed), so
//      a varchar `ruling` column (not a DB enum) keeps a future ruling value a
//      pure data event — exactly achievement_alignment_rulings.ruling's own
//      proven 'merged'-added-later precedent.
//
// TERMINALITY (pinned in tests, AxisAffinityTagger.ts): the backfill/re-tag
// pass reads the LATEST ruling per (userId, subjectId, axisKey) and NEVER
// re-writes or removes a subject's axis affinity once a ruling row exists for
// that pair — a re-tag SKIPS any (subject, axis) with a terminal ruling,
// mirroring AlignmentReviewService.computeAlignmentFlags's own "excludes any
// achievementId with a ruling row present" read.
//
// subjectId is a soft pointer (no FK) to EITHER an achievements.id or a
// dna_facets.id — subjectType disambiguates which table. No hard FK for the
// same reason achievement_alignment_rulings.achievementId has none: subjects
// are superseded/re-homed, not updated in place, and a hard FK would either
// cascade-orphan a terminal ruling or block the very correction the ruling
// itself allows.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/axis-lens-p2-neon.sql — NOT applied by this lane), mirrored
// here in Drizzle and exported from index.ts.
// =============================================================================

import { index, jsonb, pgTable, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';
import { users } from './users';

/** 'achievement' | 'facet' — which table `subjectId` points into (see file
 *  header on why this is a soft pointer, not two nullable FK columns). */
export type AxisRulingSubjectType = 'achievement' | 'facet';

/** The terminal ruling values a user (or the tagger's own confirm/remove
 *  affordance) can produce for one (subject, axis) pair:
 *   'user-confirmed'    — the corpus-card chip's confirm tap: flips a
 *                          model-sourced affinity to source:'user', terminal.
 *   'user-declined'     — the association pass's "have you done this?"
 *                          question answered no — resolves the intent with NO
 *                          tag and no retry (plan §8-pre's deniability law).
 *   'user-removed'      — the corpus-card chip's remove tap: the affinity is
 *                          stripped from the subject's axisAffinities AND this
 *                          ruling is written so a future re-tag pass never
 *                          re-adds it (removed = never re-added by re-tagging,
 *                          per the mission brief's own terminality spec).
 */
export type AxisRulingValue = 'user-confirmed' | 'user-declined' | 'user-removed';

export const axisAssociationRulings = pgTable(
  'axis_association_rulings',
  {
    id: id(),
    /** The real users.id uuid — declared FK, mirrors
     *  achievement_alignment_rulings.userId's own uuid+FK convention (every
     *  NEW table gets this from birth per CLAUDE.md's corrected 2026-06-22
     *  law), even though achievements/dna_facets themselves keep legacy
     *  text('user_id') untouched (FROZEN). */
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** Soft pointer to achievements.id or dna_facets.id — see file header. */
    subjectId: uuid('subject_id').notNull(),
    /** Which table subjectId points into. */
    subjectType: varchar('subject_type', { length: 16 }).notNull(),
    /** The role_axes.axis_key this ruling concerns. Soft-referenced (no FK) —
     *  mirrors listing_axis_reads.ListingAxisWeight.axisKey's own soft-FK
     *  reasoning (axes are edited/merged over time). */
    axisKey: varchar('axis_key', { length: 80 }).notNull(),
    /** 'user-confirmed' | 'user-declined' | 'user-removed'. varchar, not a
     *  DB enum — this codebase's house convention (see file header). */
    ruling: varchar('ruling', { length: 24 }).notNull(),
    /** The near-miss/tag evidence frozen at ruling time — the exact matched
     *  substring + score the user was actually shown, so a later cue-list
     *  change can never retroactively rewrite the historical record of what
     *  was asked/shown. Written verbatim, never regenerated. */
    evidenceSnapshot: jsonb('evidence_snapshot'),
    /** Which role_axes taxonomy pass this ruling was made against — lets a
     *  future taxonomy_version bump know this ruling predates it, without
     *  ever invalidating the ruling's OWN terminality (a stale-taxonomy
     *  ruling is still a terminal user decision; this is provenance, not a
     *  freshness gate). */
    taxonomyVersionAtRuling: varchar('taxonomy_version_at_ruling', { length: 16 }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userIdx: index('axis_association_rulings_user_idx').on(t.userId),
    subjectIdx: index('axis_association_rulings_subject_idx').on(t.subjectId),
    axisIdx: index('axis_association_rulings_axis_idx').on(t.axisKey),
  }),
);
